// M4-B §9 gas becomes a product, and §14 ESG, methane, flaring, carbon (+ the M5 hooks).
//   Gas & NGL volumes per well come from workstream A's stream split (well.prod.{oilToday, gasMcfToday, nglToday} on
//   prod.shale wells; reservoir `streams` × rate as a fallback). Unsplit wells sell their whole boe as crude. Gas sells through an outlet — gathering (local basis hub), interstate firm transport
//   (Henry Hub), LNG tolling (HH + netback, take-or-pay), FLNG, an LNG SPA (oil-indexed), or the implicit associated-gas
//   line offshore — or it is flared (FLARING_RULES: capture targets curtail oil, no-routine regimes shut wells in onshore,
//   offshore reinjects, Nigeria/West Africa penalties). Wet gas pays a processing fee (own plant or third party) and
//   sells NGLs at nglPrice(WTI). Methane, flaring CO₂, carbon prices, 45Q credits, ESG loan spread, super-emitters and
//   the zero-flaring year stat are accounted here. WEC is never charged before 2034; the M5 carbon tax hook returns 0.
import {
  gasHubPrice, henryHub, nglPrice, lngNetback, LNG, MMBTU_PER_MCF, MCF_PER_BOE, FLARING_RULES, METHANE_RULES, carbonPrice,
  CO2_T_PER_BOE, credit45q, CREDIT_45Q, carbonTaxPerBblM5, ESG_PRESSURE, ESG, stepAt, lerpAt, SEVERANCE_M4, M4_FROM,
} from '../../data/m4/rules.js';
import { streamsOf } from '../../data/m4/fields.js';
import { allWells } from '../indexes.js';
import { eqDef, fieldDef, stat, eraDollars, isAvailable, yf, yearOf } from '../defs.js';
import { fieldTransports } from '../../data/fields.js';
import { royaltyRate } from '../production.js';
import { m3TransportBlocked, transportBlockReason } from '../m3/megaprojects.js';
import { platforms } from '../m3/opsutil.js';
import { emit } from '../../core/bus.js';
import { dayToDate } from '../../core/time.js';
import { spend, earn, notify, fmt$ } from '../util.js';
import { mk, peek, m4On, rand, resDefOf, isOffshoreField, isUsRegionId, r2, clamp01 } from './marketCore.js';
import { brentToday, wtiToday } from './marketPrices.js';

export const CH4_T_PER_MCF = 0.0192;          // tonnes of methane in 1,000 scf
export const DEFAULT_FLARE_EFF = 0.9;         // a pipe and a pilot, no engineered flare tip (est.)
export const ASSOCIATED_COST_PER_MCF = 0.5;   // implicit offshore gas line / tie-in ($/Mcf, 2000 $, est.)
export const REINJECTION_COST_PER_MCF = 0.3;  // compressing gas back into the reservoir (2010 $, est.)
export const THIRD_PARTY_PROCESSING = { feePerMcf: 0.6, nglRecovery: 0.85, baseYear: 2014 };
export const TIER_EXCESS_SHARE = 0.3;         // Alberta TIER charges emissions above the facility benchmark (est.)
export const SUPER_EMITTER = { from: 2016, perDay: 0.0002, monitorMult: 0.3 };
export const TEXAS_PERMIT = { feePerPermit: 5000, baseYear: 2019 };
export const LNG_PAUSE = { from: 2024.54, until: 2025.38 };   // no new LNG SPAs (REGIMES_M4.lng_pause_2024)
const GAS_PRODUCTS = new Set(['natural_gas', 'lng']);

// ---- volumes ---------------------------------------------------------------------------------------------------
// Workstream A splits shale wells' boe into streams in production (only the crude reaches storage) and leaves today's
// volumes on well.prod.{oilToday, gasMcfToday, nglToday}. Wells without a split (M1-M3 path, incl. M4 conventional &
// deepwater reservoirs) sell their whole boe as crude, so they market no separate gas here (no double count).
export const wellSplitsGas = (w) => !!(w?.prod && (w.prod.shale || w.prod.gasMcfToday != null));
export function wellMakesGas(w) {
  if (!wellSplitsGas(w)) return false;
  const st = w.prod.streams ?? streamsOf(resDefOf(w.fieldId, w.reservoirId));
  return (st.gas ?? 0) > 0 || (st.ngl ?? 0) > 0;
}
export function wellStreamsToday(state, w) {
  const p = w.prod;
  if (!p || !(p.rate > 0) || w.ownerOperatorId || !wellSplitsGas(w)) return null;
  if (p.gasMcfToday != null) return { oil: p.oilToday ?? 0, gasMcf: p.gasMcfToday ?? 0, ngl: p.nglToday ?? 0 };
  const st = p.streams ?? streamsOf(resDefOf(w.fieldId, w.reservoirId));
  return { oil: p.rate * (st.oil ?? 0), gasMcf: p.rate * (st.gas ?? 0) * MCF_PER_BOE, ngl: p.rate * (st.ngl ?? 0), fallback: true };
}

function fieldItems(state, fieldId) {
  const out = [];
  for (const it of Object.values(state.items)) if (it.loc?.type === 'field' && it.loc.id === fieldId) out.push(it);
  return out;
}
const hasDef = (items, id) => items.some((it) => it.defId === id);
const flngAt = (state, fieldId) => Object.values(platforms(state)).some((p) => p.defId === 'flng' && p.fieldId === fieldId && p.status !== 'BUILDING');

// ---- outlets ---------------------------------------------------------------------------------------------------
function defBlocked(state, fieldId, def) { return m3TransportBlocked(state, fieldId, def) || !isAvailable(def, state); }
export function gasOutlet(state, fieldId) {
  const fdef = fieldDef(fieldId);
  const f = state.fields[fieldId];
  if (!fdef || !f) return null;
  if (flngAt(state, fieldId)) return { kind: 'flng', defId: 'flng', name: 'Floating LNG' };
  const G = peek(state)?.gas?.[fieldId];
  const cands = [];
  if (G?.outlet) cands.push(G.outlet);
  if (f.transportId && GAS_PRODUCTS.has(eqDef(f.transportId)?.stats?.product)) cands.push(f.transportId);
  for (const id of f.connected ?? []) if (GAS_PRODUCTS.has(eqDef(id)?.stats?.product)) cands.push(id);
  for (const id of cands) {
    const d = eqDef(id);
    if (!d || defBlocked(state, fieldId, d)) continue;
    const kind = d.stats.product === 'lng' ? 'lng' : stat(d, 'firm', false) ? 'interstate' : 'gathering';
    return { kind, defId: id, name: d.name, costPerMcf: eraDollars(state, stat(d, 'costPerMcf', 0.5), d.baseYear ?? d.year) };
  }
  // no gas transport in the field's list at all (offshore tie-backs): associated gas rides the export line
  const hasGasOption = (fdef.transport ?? []).some((t) => GAS_PRODUCTS.has(eqDef(t.split('@')[0])?.stats?.product));
  const hub = fdef.gasHub ?? 'henry_hub';
  if (!hasGasOption && hub !== 'none' && f.transportId) return { kind: 'associated', defId: null, name: 'Associated-gas export line', costPerMcf: eraDollars(state, ASSOCIATED_COST_PER_MCF, 2000) };
  return null;
}

// $/Mcf the field's gas fetches through an outlet today (before royalty/severance).
export function gasPricePerMcf(state, fieldId, outlet) {
  const y = yf(state);
  const fdef = fieldDef(fieldId);
  const hub = fdef?.gasHub ?? 'henry_hub';
  if (!outlet) return { price: 0, gross: 0, cost: 0, hubPrice: gasHubPrice(hub, y) };
  let gross = 0, cost = outlet.costPerMcf ?? 0;
  if (outlet.kind === 'gathering' || outlet.kind === 'associated') gross = gasHubPrice(hub, y) * MMBTU_PER_MCF;
  else if (outlet.kind === 'interstate') gross = henryHub(y) * MMBTU_PER_MCF;
  else if (outlet.kind === 'lng') { gross = (henryHub(y) + Math.max(lngNetback(y, 'asia'), lngNetback(y, 'europe'))) * MMBTU_PER_MCF; cost = 0; }
  else if (outlet.kind === 'flng') { gross = (gasHubPrice('jkm', y) - LNG.shippingPerMmbtu.asia - LNG.regas) * MMBTU_PER_MCF; cost = 0; }
  return { price: gross - cost, gross, cost, hubPrice: gasHubPrice(hub, y) };
}
export function spaPricePerMcf(state, outlet) {
  const y = yf(state);
  const toll = outlet?.kind === 'lng' ? (stepAt(LNG.tollPerMmbtu, y) ?? 3) + (LNG.feedGasMult - 1) * henryHub(y) : 0;
  return (LNG.oilSlopeContract * brentToday(state) - LNG.shippingPerMmbtu.asia - toll) * MMBTU_PER_MCF;
}

// ---- flaring rules -------------------------------------------------------------------------------------------------
export function flaringRule(state, fieldId) {
  const fdef = fieldDef(fieldId);
  const key = fdef?.flaring?.regime ?? 'default';
  const reg = FLARING_RULES[key] ?? FLARING_RULES.default;
  const y = yf(state);
  const before = reg.from && y < reg.from;
  let routineOk = before ? true : (fdef?.flaring?.routineAllowed ?? reg.routineAllowed ?? true);
  // EPA OOOOb: no routine flaring at US oil wells from mid-2026 (gas must be captured or the well curtailed)
  if (routineOk && y >= METHANE_RULES.epa_oooob_2023.noRoutineFlaringFrom && isUsRegionId(fdef?.regionId)) routineOk = false;
  const target = reg.captureTargets ? stepAt(reg.captureTargets, y) : null;
  const penalty = reg.penaltyPerMcf ? stepAt(reg.penaltyPerMcf, y) ?? 0 : 0;
  return { regime: key, routineOk, target, penalty, limitShare: reg.limitShare ?? 0, penaltyFrom: reg.penaltyFrom ?? null, scrutinyFrom: reg.scrutinyFrom ?? null, rule: reg };
}

// Oil curtailment share for tomorrow from gas-capture rules (0 = none). Projected capture = 1 with a working outlet
// selling the gas, 0 when the gas would be flared.
export function flaringCurtailShare(state, fieldId) {
  const R = flaringRule(state, fieldId);
  const out = gasOutlet(state, fieldId);
  const mode = peek(state)?.gas?.[fieldId]?.mode ?? 'auto';
  const capture = out && mode !== 'flare' ? 1 : 0;
  if (R.target != null) return capture >= R.target ? 0 : clamp01(1 - capture / R.target);
  if (!R.routineOk && !isOffshoreField(fieldId)) return capture >= 1 - R.limitShare ? 0 : clamp01(1 - capture);
  return 0;
}

// ---- emissions kit at a field ---------------------------------------------------------------------------------------
export function emissionsKit(state, fieldId, items = fieldItems(state, fieldId)) {
  let mult = 1, eff = DEFAULT_FLARE_EFF, monitor = false;
  const seen = new Set();
  for (const it of items) {
    const d = eqDef(it.defId);
    if (d?.cat !== 'emissions' || seen.has(d.id)) continue;
    seen.add(d.id);
    mult *= stat(d, 'methaneMult', 1);
    if (stat(d, 'flareEff', 0) > eff) eff = stat(d, 'flareEff', 0);
    if (stat(d, 'detectsSuperEmitters', false)) monitor = true;
  }
  return { methaneMult: mult, flareEff: eff, monitor, defs: [...seen] };
}
export const esgPressure = (y) => (y < M4_FROM ? 0 : lerpAt(ESG_PRESSURE, y));
// WEC ($/t CH4): repealed for 2025 and delayed to 2034 — never charged by the M4 sim (M5 may flip the rule).
export function wecPerTonne(y) {
  const W = METHANE_RULES.wec;
  if (y < W.delayedTo) return 0;
  return stepAt(W.schedule, y) ?? 0;
}
// M5 hook: the global carbon tax ($/bbl) — 0 until 2035, so the M4 carbonTax P&L line stays 0.
export const carbonTaxPerBbl = (y) => carbonTaxPerBblM5(y);

function carbonSchemes(rid, y) {
  if (rid === 'north_sea_norway') return [['eu_ets', 1], ['norway_co2', 1]];
  if (rid === 'north_sea_uk') return [[y < 2021 ? 'eu_ets' : 'uk_ets', 1]];
  if (rid === 'alberta') return [['alberta_tier', TIER_EXCESS_SHARE]];
  return [];
}
export function carbonPricePerT(rid, y) { return carbonSchemes(rid, y).reduce((a, [s, share]) => a + carbonPrice(s, y) * share, 0); }
function co2KindFor(fieldId) {
  const f = fieldDef(fieldId);
  if (f?.oilSands?.extraction === 'mining') return 'oil_sands_mining';
  if (f?.oilSands) return 'oil_sands_sagd';
  if (f?.heavyOil) return 'extra_heavy';
  return isOffshoreField(fieldId) ? 'deepwater' : 'upstream_light';
}

// ESG loan spread (bps) for the company: index × how high-flaring / high-methane its gas is (trailing year).
export function esgLoanBps(state) {
  const y = yf(state);
  if (y < M4_FROM) return 0;
  const E = peek(state)?.esg;
  if (!E) return 0;
  const src = E.prevYear && E.prevYear.gasMcf > 0 ? E.prevYear : E;
  if (!(src.gasMcf > 0)) return 0;
  const flareShare = src.flaredMcf / src.gasMcf;
  const intensity = (src.methaneT / CH4_T_PER_MCF) / src.gasMcf;
  const score = clamp01(Math.max(flareShare / 0.2, intensity / 0.015));
  return ESG.loanRateBpsAtFull * esgPressure(y) * score;
}

// ---- daily gas marketing, flaring & emissions (tickPost) -----------------------------------------------------------
function gasRec(state, fieldId) { const M = mk(state); return (M.gas[fieldId] ??= { outlet: null, mode: 'auto' }); }

export function tickGas(state) {
  const y = yf(state);
  const byField = new Map();
  for (const w of allWells(state)) {
    if (w.status !== 'PRODUCING' || !w.prod || w.ownerOperatorId || !(w.prod.rate > 0)) continue;
    let F = byField.get(w.fieldId);
    if (!F) byField.set(w.fieldId, (F = { boe: 0, gas: 0, ngl: 0 }));
    F.boe += w.prod.rate;
    const st = wellStreamsToday(state, w);
    if (st) { F.gas += st.gasMcf ?? 0; F.ngl += st.ngl ?? 0; }
  }
  const M = mk(state);
  const E = rollEsgYear(state, M);
  const esgIdx = esgPressure(y);
  const L = M.ledger;
  let gasToday = 0, flaredToday = 0;
  for (const [fid, F] of byField) {
    const fdef = fieldDef(fid);
    const rid = fdef?.regionId;
    const items = fieldItems(state, fid);
    const kit = emissionsKit(state, fid, items);
    // carbon on production (EU/Norway/UK ETS, Alberta TIER)
    const cp = carbonPricePerT(rid, y);
    const prodCo2 = F.boe * (CO2_T_PER_BOE[co2KindFor(fid)] ?? 0.02);
    let co2 = prodCo2, carbonCost = 0;
    // carbonTax (M5 hook): 0 in M4
    const ctax = carbonTaxPerBbl(y) * F.boe;
    if (ctax > 0) { spend(state, ctax, 'carbonTax', false); L.carbonTax += ctax; }
    if (!(F.gas > 0) && !(F.ngl > 0)) {
      if (cp > 0) { carbonCost = prodCo2 * cp; spend(state, carbonCost, 'carbon', false); L.carbon += carbonCost; }
      E.co2T += co2;
      continue;
    }
    const G = gasRec(state, fid);
    const out = gasOutlet(state, fid);
    const R = flaringRule(state, fid);
    const pr = gasPricePerMcf(state, fid, out);
    let sell = !!out && G.mode !== 'flare';
    if (out && G.mode === 'flare' && !R.routineOk) sell = true;
    if (sell && G.mode === 'auto' && pr.price < 0 && R.routineOk) sell = false; // Waha below zero: flare rather than pay
    let flared = 0, reinjected = 0;
    if (sell) {
      let gasRev = 0;
      // SPA volume first (oil-indexed), the rest at the outlet price
      const spa = M.spa[fid];
      let atSpa = 0;
      if (spa && state.day < spa.untilDay && (out.kind === 'lng' || out.kind === 'flng')) {
        atSpa = Math.min(F.gas, spa.volumeMcfd);
        gasRev += atSpa * spaPricePerMcf(state, out);
        const short = spa.volumeMcfd - atSpa;
        if (short > 0) { const pen = short * Math.max(0, spaPricePerMcf(state, out)) * 0.1; spend(state, pen, 'transport', false); L.takeOrPay += pen; }
      }
      const rest = F.gas - atSpa;
      const gross = rest * pr.gross;
      gasRev += gross;
      const roy = Math.max(0, gasRev) * royaltyRate(state, fid);
      const sevRate = typeof SEVERANCE_M4[rid]?.gas === 'number' ? SEVERANCE_M4[rid].gas : 0;
      const sev = Math.max(0, gasRev) * sevRate;
      earn(state, gasRev, 'gasSales');
      if (roy > 0) spend(state, roy, 'royalties', false);
      if (sev > 0) spend(state, sev, 'severance', false);
      if (rest * pr.cost > 0) spend(state, rest * pr.cost, 'transport', false);
      L.gasSales += gasRev;
      // NGLs: own plant (capacity, opex) or a third-party processor
      if (F.ngl > 0) {
        const plantDef = eqDef('gas_processing_plant');
        const plants = items.filter((it) => it.defId === 'gas_processing_plant').length;
        const cap = plants * stat(plantDef, 'capacityMcfd', 200000);
        const own = Math.min(F.gas, cap), third = F.gas - own;
        const fee = own * eraDollars(state, stat(plantDef, 'opexPerMcf', 0.25), plantDef?.baseYear ?? plantDef?.year ?? 2005)
          + third * eraDollars(state, THIRD_PARTY_PROCESSING.feePerMcf, THIRD_PARTY_PROCESSING.baseYear);
        const rec = F.gas > 0 ? (own * stat(plantDef, 'nglRecovery', 0.9) + third * THIRD_PARTY_PROCESSING.nglRecovery) / F.gas : THIRD_PARTY_PROCESSING.nglRecovery;
        const nglRev = F.ngl * rec * nglPrice(Math.max(0, wtiToday(state)), y);
        earn(state, nglRev, 'gasSales');
        if (fee > 0) spend(state, fee, 'opex', false);
        L.nglSales += nglRev;
      }
      // LNG tolling: take-or-pay on the contracted volume; first cargo
      if (out.kind === 'lng') {
        const C = (M.lng[fid] ??= { contractMcfd: Math.max(10000, Math.round(F.gas)), startDay: state.day, untilDay: state.day + Math.round(stat(eqDef(out.defId), 'termYears', 20) * 365) });
        const short = Math.max(0, C.contractMcfd - F.gas);
        if (short > 0 && state.day < C.untilDay) { const t = short * (stepAt(LNG.tollPerMmbtu, y) ?? 3) * MMBTU_PER_MCF; spend(state, t, 'transport', false); L.takeOrPay += t; }
      }
      if ((out.kind === 'lng' || out.kind === 'flng') && !M.firstLngDay) {
        M.firstLngDay = state.day;
        state.company.stats.firstLngCargoDay = state.day; // front-page trigger (firstLngCargo) for the news workstream
        notify(state, 'news', { fieldId: fid, kind: 'market', text: `First LNG cargo: ${fdef.name} gas leaves as liquid, bound for ${out.kind === 'flng' ? 'Asia' : 'Asia or Europe'}.` });
      }
      if (out.kind === 'lng' || out.kind === 'flng') state.company.stats.lngSalesMcf = (state.company.stats.lngSalesMcf ?? 0) + F.gas;
    } else if (!R.routineOk && isOffshoreField(fid)) {
      reinjected = F.gas;
      const c = reinjected * eraDollars(state, REINJECTION_COST_PER_MCF, 2010);
      spend(state, c, 'opex', false);
    } else {
      flared = F.gas;
      let pen = flared * R.penalty;
      if (R.penaltyFrom && y >= R.penaltyFrom && R.limitShare >= 0) pen += flared * Math.max(0, 1 - R.limitShare) * 0.5;
      if (R.scrutinyFrom && y >= R.scrutinyFrom) {
        G.flareDays = (G.flareDays ?? 0) + 1;
        if (G.flareDays > (R.rule.maxDaysWithoutPermit ?? 10) && !(G.permitUntil > state.day)) { G.permitUntil = state.day + (R.rule.permitDays ?? 45); pen += eraDollars(state, TEXAS_PERMIT.feePerPermit, TEXAS_PERMIT.baseYear); }
      }
      if (pen > 0) { spend(state, pen, 'carbon', false); L.flarePenalty += pen; }
      if (!G.flareWarned) {
        G.flareWarned = state.day;
        notify(state, 'news', { fieldId: fid, kind: 'regulation', level: 'warn', text: `${fdef.name}: no gas outlet — ${Math.round(flared).toLocaleString('en-US')} Mcf/d is being flared${R.target != null ? ` (capture target ${Math.round(R.target * 100)}%: oil will be curtailed)` : ''}. Connect gas gathering to sell it.` });
      }
    }
    // methane & CO2
    const leakMcf = F.gas * METHANE_RULES.leakRateBaseline * kit.methaneMult + flared * (1 - kit.flareEff);
    const ch4 = leakMcf * CH4_T_PER_MCF;
    co2 += flared * kit.flareEff * CO2_T_PER_BOE.flared_mcf;
    if (cp > 0) { carbonCost = co2 * cp; spend(state, carbonCost, 'carbon', false); L.carbon += carbonCost; }
    state.company.stats.methaneT = (state.company.stats.methaneT ?? 0) + ch4;
    E.gasMcf += F.gas; E.flaredMcf += flared; E.routineFlaredMcf += flared; E.methaneT += ch4; E.co2T += co2; E.reinjectedMcf = (E.reinjectedMcf ?? 0) + reinjected;
    gasToday += F.gas; flaredToday += flared;
    // reputation: flaring under ESG pressure, super-emitters
    if (flared > 0 && esgIdx > 0) E.repDebt += ESG.repPerFlareMmcf * (flared / 1000) * (esgIdx / 0.5);
    if (y >= SUPER_EMITTER.from && rand(state, `se:${fid}`) < SUPER_EMITTER.perDay * kit.methaneMult * (kit.monitor ? SUPER_EMITTER.monitorMult : 1)) {
      state.company.reputation = Math.max(0, state.company.reputation - ESG.repPerSuperEmitter);
      E.superEmitters = (E.superEmitters ?? 0) + 1;
      notify(state, 'news', { fieldId: fid, kind: 'regulation', level: 'warn', text: `A satellite catches a methane plume over ${fdef.name}: a stuck dump valve venting for days. Reputation −${ESG.repPerSuperEmitter}.` });
    }
  }
  if (gasToday > 0) E.gasDays = (E.gasDays ?? 0) + 1;
  if (E.repDebt >= 0.1) { const d = Math.floor(E.repDebt * 10) / 10; state.company.reputation = Math.max(0, state.company.reputation - d); E.repDebt -= d; }
  // 45Q: captured & stored CO2 (US)
  tick45q(state, M);
  return { gasToday, flaredToday };
}

function rollEsgYear(state, M) {
  const { year } = dayToDate(state.day);
  const E = M.esg;
  if (E.year == null) E.year = year;
  if (E.year !== year) {
    const prev = { year: E.year, gasMcf: Math.round(E.gasMcf), flaredMcf: Math.round(E.flaredMcf), routineFlaredMcf: Math.round(E.routineFlaredMcf), methaneT: r2(E.methaneT), co2T: Math.round(E.co2T), gasDays: E.gasDays ?? 0 };
    if (prev.gasMcf > 0 && prev.routineFlaredMcf === 0 && prev.gasDays >= 300) state.company.stats.zeroFlareYears = (state.company.stats.zeroFlareYears ?? 0) + 1;
    E.prevYear = prev;
    E.year = year; E.gasMcf = 0; E.flaredMcf = 0; E.routineFlaredMcf = 0; E.methaneT = 0; E.co2T = 0; E.gasDays = 0; E.reinjectedMcf = 0; E.superEmitters = 0;
  }
  return E;
}

function tick45q(state, M) {
  const y = yf(state);
  if (y < CREDIT_45Q.storage[0][0]) return;
  const byField = {};
  for (const it of Object.values(state.items)) {
    if (it.loc?.type !== 'field') continue;
    const d = eqDef(it.defId);
    if (d?.cat !== 'emissions' && d?.cat !== 'midstream' && d?.cat !== 'oil_sands') continue;
    const r = (byField[it.loc.id] ??= { capture: 0, storage: 0, source: false, opexCapture: 0, opexStore: 0 });
    if (d.id === 'carbon_capture_unit') { r.capture += stat(d, 'captureTpy', 0); r.opexCapture = stat(d, 'opexPerT', 30); r.captureBase = d.baseYear ?? 2015; }
    if (d.id === 'ccs_injection_well') { r.storage += stat(d, 'storageTpy', 0); r.opexStore = stat(d, 'opexPerT', 8); r.storeBase = d.baseYear ?? 2020; }
    if (['gas_plant', 'upgrader', 'steam_plant'].includes(stat(d, 'kind', ''))) r.source = true;
  }
  for (const [fid, r] of Object.entries(byField)) {
    if (!(r.capture > 0) || !(r.storage > 0) || !r.source) continue;
    const tpy = Math.min(r.capture, r.storage);
    if (tpy < (stepAt(CREDIT_45Q.minCaptureTpy, y) ?? 0)) continue;
    const t = tpy / 365;
    const C = (M.ccs ??= {});
    const rec = (C[fid] ??= { since: state.day, storedT: 0 });
    const opex = t * (eraDollars(state, r.opexCapture, r.captureBase) + eraDollars(state, r.opexStore, r.storeBase));
    spend(state, opex, 'opex', false);
    rec.storedT += t;
    const credit = isUsRegionId(fieldDef(fid)?.regionId) && state.day - rec.since < CREDIT_45Q.creditYears * 365 ? t * credit45q('storage', y) : 0;
    if (credit > 0) { earn(state, credit, 'credits45q'); M.ledger.credits45q += credit; }
  }
}

// ---- curtailment by rotating wells (flaring capture rules, Alberta) — tickPre -----------------------------------------
export function applyCurtailments(state, shares) {
  const M = mk(state);
  for (const [fid, s] of Object.entries(shares)) {
    const C = (M.curtail[fid] ??= { carry: 0, rot: 0, share: 0, reason: null });
    C.share = r2(s.share); C.reason = s.reason;
    if (!(s.share > 0)) { delete M.curtail[fid]; continue; }
    const ws = allWells(state).filter((w) => w.fieldId === fid && w.status === 'PRODUCING' && w.prod && !w.prod.shutIn && !w.ownerOperatorId && (s.gasOnly ? wellMakesGas(w) : true));
    if (!ws.length) continue;
    C.carry += s.share * ws.length;
    let k = Math.min(ws.length, Math.floor(C.carry + 1e-9));
    C.carry -= k;
    for (let i = 0; i < k; i++) {
      const w = ws[(C.rot + i) % ws.length];
      w.prod.forcedUntil = Math.max(w.prod.forcedUntil ?? 0, state.day + 1);
      w.prod.curtailedBy = s.reason;
    }
    C.rot = (C.rot + k) % ws.length;
    if (s.share >= 0.05 && C.warnedDay == null) {
      C.warnedDay = state.day;
      notify(state, 'news', { fieldId: fid, kind: 'regulation', level: 'warn', text: `${fieldDef(fid)?.name}: ${s.reason === 'alberta' ? 'Alberta’s curtailment order' : 'the gas-capture rule'} holds back ${Math.round(s.share * 100)}% of your oil.` });
      if (s.reason === 'flaring') try { emit('flaringBreach', { fieldId: fid, share: s.share }); } catch (e) { /* UI */ }
    }
  }
  for (const fid of Object.keys(M.curtail)) if (!shares[fid]) delete M.curtail[fid];
}

// ---- actions --------------------------------------------------------------------------------------------------
export function setGasOutletImpl(state, fieldId, defId) {
  if (!m4On(state)) return { ok: false, msg: 'Gas marketing opens with the M4 era (2000).' };
  const f = state.fields[fieldId];
  const fdef = fieldDef(fieldId);
  if (!f || !fdef) return { ok: false, msg: 'No such field.' };
  const G = gasRec(state, fieldId);
  if (defId == null) { G.outlet = null; return { ok: true, msg: `${fdef.name}: gas outlet cleared.` }; }
  const def = eqDef(defId);
  if (!def || def.cat !== 'transport' || !GAS_PRODUCTS.has(def.stats?.product)) return { ok: false, msg: 'Not a gas transport.' };
  if (!fieldTransports(fdef, yearOf(state)).includes(defId)) return { ok: false, msg: `${def.name} doesn't serve ${fdef.name} (yet).` };
  if (!isAvailable(def, state)) return { ok: false, msg: `${def.name} is not available yet.` };
  const why = transportBlockReason(state, fieldId, def);
  if (why) return { ok: false, msg: why };
  if (!f.connected.includes(defId)) {
    const cost = eraDollars(state, stat(def, 'connectCost', 0), def.baseYear ?? def.year) || 0;
    if (cost > 0 && !spend(state, cost, 'capex')) return { ok: false, msg: `Connecting costs ${fmt$(cost)}.` };
    f.connected.push(defId);
  }
  G.outlet = defId;
  return { ok: true, msg: `${fdef.name} gas now goes to ${def.name}.` };
}
export function setGasModeImpl(state, fieldId, mode = 'auto') {
  if (!state.fields[fieldId]) return { ok: false, msg: 'No such field.' };
  if (!['auto', 'sell', 'flare'].includes(mode)) return { ok: false, msg: 'Mode is auto, sell or flare.' };
  gasRec(state, fieldId).mode = mode;
  return { ok: true, msg: mode === 'flare' ? 'Flaring the gas (where the rules allow it).' : mode === 'sell' ? 'Selling all gas, even below zero.' : 'Selling gas; flaring only when the price is negative and flaring is allowed.' };
}
export function signLngSpaImpl(state, fieldId) {
  if (!m4On(state)) return { ok: false, msg: 'LNG contracts open with the M4 era.' };
  const y = yf(state);
  if (y >= LNG_PAUSE.from && y < LNG_PAUSE.until) return { ok: false, msg: 'Washington has paused new LNG export approvals: no new SPAs until the pause is lifted.' };
  const out = gasOutlet(state, fieldId);
  if (!out || (out.kind !== 'lng' && out.kind !== 'flng')) return { ok: false, msg: 'An SPA needs liquefaction: an LNG export toll or a floating LNG vessel.' };
  const M = mk(state);
  if (M.spa[fieldId] && state.day < M.spa[fieldId].untilDay) return { ok: false, msg: 'This field already sells under an SPA.' };
  const d = eqDef('lng_spa');
  M.spa[fieldId] = { volumeMcfd: stat(d, 'volumeMcfd', 100000), slope: LNG.oilSlopeContract, startDay: state.day, untilDay: state.day + Math.round(stat(d, 'termYears', 20) * 365) };
  return { ok: true, msg: `Signed a 20-year SPA: ${stat(d, 'volumeMcfd', 100000).toLocaleString('en-US')} Mcf/d at ${Math.round(LNG.oilSlopeContract * 100)}% of Brent (${fmt$(spaPricePerMcf(state, out) / MMBTU_PER_MCF)}/MMBtu net today).` };
}

// ---- views -------------------------------------------------------------------------------------------------------
export function gasMarketViewImpl(state, fieldId = null) {
  const y = yf(state);
  const on = y >= M4_FROM;
  const hubs = on ? { henry_hub: r2(henryHub(y)), waha: r2(gasHubPrice('waha', y)), appalachia: r2(gasHubPrice('appalachia', y)), aeco: r2(gasHubPrice('aeco', y)), ttf: r2(gasHubPrice('ttf', y)), nbp: r2(gasHubPrice('nbp', y)), jkm: r2(gasHubPrice('jkm', y)) } : null;
  const out = { year: y, active: on, hubs, ngl: on ? r2(nglPrice(Math.max(0, wtiToday(state)), y)) : null, lngNetback: on ? { asia: r2(lngNetback(y, 'asia')), europe: r2(lngNetback(y, 'europe')) } : null,
    lngFirstExportDay: LNG.firstExportDay, lngPause: y >= LNG_PAUSE.from && y < LNG_PAUSE.until, field: null };
  if (fieldId && on && fieldDef(fieldId)) {
    const fdef = fieldDef(fieldId);
    const o = gasOutlet(state, fieldId);
    const pr = gasPricePerMcf(state, fieldId, o);
    const R = flaringRule(state, fieldId);
    let gas = 0, ngl = 0;
    for (const w of allWells(state)) if (w.fieldId === fieldId) { const st = wellStreamsToday(state, w); if (st) { gas += st.gasMcf ?? 0; ngl += st.ngl ?? 0; } }
    const items = fieldItems(state, fieldId);
    const options = (fdef.transport ?? []).map((t) => t.split('@')).filter(([id]) => GAS_PRODUCTS.has(eqDef(id)?.stats?.product)).map(([id, yy]) => {
      const d = eqDef(id);
      return { defId: id, name: d.name, from: +(yy ?? d.year), available: yearOf(state) >= +(yy ?? 0) && isAvailable(d, state) && !m3TransportBlocked(state, fieldId, d), blocked: transportBlockReason(state, fieldId, d), connected: state.fields[fieldId]?.connected?.includes(id) ?? false };
    });
    out.field = { fieldId, gasHub: fdef.gasHub ?? 'henry_hub', hubPrice: r2(pr.hubPrice), outlet: o, pricePerMcf: r2(pr.price), grossPerMcf: r2(pr.gross), gasMcfd: Math.round(gas), nglBbld: Math.round(ngl),
      mode: peek(state)?.gas?.[fieldId]?.mode ?? 'auto', flaring: { regime: R.regime, routineAllowed: R.routineOk, captureTarget: R.target, penaltyPerMcf: R.penalty, curtailShare: r2(flaringCurtailShare(state, fieldId)) },
      processing: { plants: items.filter((it) => it.defId === 'gas_processing_plant').length, thirdPartyFee: r2(eraDollars(state, THIRD_PARTY_PROCESSING.feePerMcf, THIRD_PARTY_PROCESSING.baseYear)) },
      options, lng: peek(state)?.lng?.[fieldId] ?? null, spa: peek(state)?.spa?.[fieldId] ?? null, spaPricePerMcf: o && (o.kind === 'lng' || o.kind === 'flng') ? r2(spaPricePerMcf(state, o)) : null, flng: flngAt(state, fieldId) };
  }
  return out;
}

export function esgViewImpl(state) {
  const y = yf(state);
  const M = peek(state);
  const E = M?.esg ?? null;
  return {
    year: y, active: y >= M4_FROM, pressure: r2(esgPressure(y)), loanBps: Math.round(esgLoanBps(state)),
    thisYear: E ? { gasMcf: Math.round(E.gasMcf), flaredMcf: Math.round(E.flaredMcf), methaneT: r2(E.methaneT), co2T: Math.round(E.co2T), superEmitters: E.superEmitters ?? 0 } : null,
    lastYear: E?.prevYear ?? null, methaneT: r2(state.company.stats.methaneT ?? 0), zeroFlareYears: state.company.stats.zeroFlareYears ?? 0,
    rules: { leakRateBaseline: METHANE_RULES.leakRateBaseline, oooo: y >= METHANE_RULES.epa_oooo_2012.from, greenCompletions: y >= METHANE_RULES.epa_oooo_2012.greenCompletionsFrom, oooob: y >= METHANE_RULES.epa_oooob_2023.from,
      wecPerTonne: wecPerTonne(y), wecNote: 'Waste Emissions Charge disapproved (CRA, 14 Mar 2025) and delayed to 2034: never charged in M4.', euMethaneImportsFrom: METHANE_RULES.eu_methane_reg.mrvImportsFrom },
    carbonPrices: { eu_ets: r2(carbonPrice('eu_ets', y)), uk_ets: r2(carbonPrice('uk_ets', y)), norway_co2: r2(carbonPrice('norway_co2', y)), alberta_tier: r2(carbonPrice('alberta_tier', y)), canada_fed: r2(carbonPrice('canada_fed', y)) },
    credit45q: { storage: credit45q('storage', y), eor: credit45q('eor', y) }, ccs: M?.ccs ? JSON.parse(JSON.stringify(M.ccs)) : {},
    carbonTaxPerBbl: carbonTaxPerBbl(y), ledger: M?.ledger ? { ...M.ledger } : null,
  };
}
